import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { isNil } from "lodash";
import { of, Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationMessageRowComponent } from "../../../../../validation/validation-message-row/validation-message-row.component";
import { ButtonComponent } from "../../../../button/button.component";
import { InputComponent } from "../../../../input/input.component";
import { SelectorComponent } from "../../../../selector/selector.component";
import { TogglePanelComponent } from "../../../../toggle-panel/toggle-panel.component";
import { IMapComponent } from "../../../map.models";
import { IMapGeometry, IMapSettings } from "../../../map.settings";
import { MapPopupService } from "../../widgets/map-popup/map-popup.service";
import { MapBaseSubComponent } from "../map-base-sub-component/map-base-sub.component";
import { FastighetGeometry } from "./map-fastighet-models";
import { MapFastighetService } from "./map-fastighet.service";

export const mapFastighetWidgetId = "map-fastighet";

@Component({
    selector: "rask-map-fastighet",
    templateUrl: "./map-fastighet.component.html",
    styleUrls: ["./map-fastighet.component.scss"],
    providers: [
        { provide: MapBaseSubComponent,
            useExisting: forwardRef(() => MapFastighetComponent) }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TogglePanelComponent,
        ValidationMessageRowComponent,
        ButtonComponent,
        SelectorComponent,
        FormsModule,
        InputComponent
    ]
})
export class MapFastighetComponent extends MapBaseSubComponent implements OnInit, OnDestroy {
    readonly idDrawlayer = "fastigheter";
    readonly idFeaturelayer = "MetriaFastighetsgranser";

    widgetId = mapFastighetWidgetId;
    buttons = [
        {
            iconClass: "far fa-house",
            tooltipText: "Fastighet",
            visible: () => this.isEnabled(),
            disabled: this.disabled,
            widgetId: this.widgetId
        }
    ];
    componentReady = false;
    draw: any;
    @Output() fastighet = new EventEmitter<FastighetGeometry>();
    fastigheterHistory: FastighetGeometry[] = [];
    fastighetsbeteckning: string;
    @Input() importEnabled = false;
    kommun: any;
    map: IMapComponent;
    mapsettings: IMapSettings;
    panelFormExpanded = false;
    panelMapExpanded = false;
    view: any;

    private readonly $ngDestroyed = new Subject<void>();
    private readonly mapFastighetService = inject(MapFastighetService);
    private readonly mapPopupService = inject(MapPopupService);

    private clickActivated: boolean;
    private isFastighetlayerVisible = false;

    constructor() {
        super();
    }

    ngOnDestroy(): void {
        this.$ngDestroyed.next();
        this.$ngDestroyed.complete();
    }

    ngOnInit(): void {
        this.mapReady.pipe(
            takeUntil(this.$ngDestroyed)
        ).subscribe(instance => {
            this.map = instance;
            this.mapFastighetService.init(instance);
        });
        this.onShow.pipe(takeUntil(this.$ngDestroyed)).subscribe(() => {
            this.isFastighetlayerVisible = this.map?.isLayerVisible(this.idFeaturelayer);
            if (!this.isFastighetlayerVisible) {
                this.map?.showFeatureLayer(this.idFeaturelayer);
            }
        });
        this.onHide.pipe(takeUntil(this.$ngDestroyed)).subscribe(() => {
            this.reset();
            if (!this.isFastighetlayerVisible) {
                this.map?.hideFeatureLayer(this.idFeaturelayer);
            }
        });
        this.mapFastighetService.fastigheter$
            .pipe(takeUntil(this.$ngDestroyed), filter(f => !ObjectUtils.isNullOrUndefined(f)))
            .subscribe(f => {
                this.fastigheterHistory = f;
            });

        this.mapFastighetService.setImportEnabled(this.importEnabled);

        this.mapFastighetService.import$
            .pipe(takeUntil(this.$ngDestroyed), filter(f => !ObjectUtils.isNullOrUndefined(f)))
            .subscribe(f => {
                if (this.importEnabled) {
                    this.fastighet.emit(f);
                }
            });
    }

    onInit(view: any, draw: any): void {
        this.draw = draw;
        this.view = view;
        this.componentReady = true;
        this.mapFastighetService.enableKarta();
        this.mapFastighetService.preinit(this.mapsettings, view);
        this.mapFastighetService.setSelectFastighet(event => this.selectFastighet(event));
    }

    preInit(mapsettings: IMapSettings) {
        this.mapsettings = mapsettings;

        if (this.mapsettings.drawLayers.find(l => l.id === this.idDrawlayer) === undefined) {
            this.mapsettings.drawLayers.push({
                id: this.idDrawlayer,
                title: "Fastigheter",
                visible: true,
                readonly: true,
                geometries: [],
                selectable: true,
                hideInLayerList: true,
                onClick: (mouse, geometry) => {
                    this.clickFastighetGeometry(mouse, geometry);
                    return false;
                },
                onHoverIn: () => true,
                onHoverOut: () => true
            });
        }
    }

    protected activateClickInMap(): void {
        this.clickActivated = true;
        this.mapFastighetService.enableKarta();
        this.mapFastighetService.enableSearchByMapClick();
        this.mapFastighetService.map.setClass(["interact-crosshair"]);
    }

    protected clickCancel() {
        this.cancel();
        this.clickActivated = false;
        this.panelMapExpanded = false;
        this.panelFormExpanded = false;
    }

    protected clickFastighetGeometry(mouse, geometry): void {
        if (this.panelFormExpanded || this.panelMapExpanded ) {
            this.mapFastighetService.openPopupOnGeometry(mouse, geometry, this.importEnabled);
        }
    }

    protected clickFastighetHistory(history: FastighetGeometry): void {
        this.clickCancel();
        this.map.gotoGeometries([history.geometry], true);
        setTimeout(() => {
            this.openPopupOnGeometry(history.geometry);
            this.map.updatePopupPosition();
        }, 500);
    }

    protected formSearchDisabled() {
        return !ObjectUtils.stringHasValue(this.kommun?.namn?.trim()) || !ObjectUtils.stringHasValue(this.fastighetsbeteckning?.trim());
    }

    protected hasFastigheterHistory(): boolean {
        return this.fastigheterHistory?.length > 0;
    }

    protected isClickActivated(): boolean {
        return this.clickActivated;
    }

    protected isEnabled() {
        return !this.hideButton;
    }

    protected isNothingSelected() {
        return !this.mapFastighetService.hasSelection;
    }

    protected kommunFormatter = (vk: ITypeAheadResult) => vk ? vk : "";

    protected kommunSearch = (input: string) => {
        if (input?.length >= 3) {
            return this.mapFastighetService.kommunSearch(input);
        } else {
            return of([]);
        }
    };

    protected kommunViewFormatter = (kommun: ITypeAheadResult) => kommun ? `${kommun.namn}` : null;

    protected onToggleFormSearch(visible) {
        this.panelFormExpanded = visible;
        if (visible) {
            this.panelMapExpanded = false;
        } else {
            this.cancel();
        }
    }

    protected onToggleMapSearch(visible) {
        this.panelMapExpanded = visible;
        if (visible) {
            this.panelFormExpanded = false;
        } else {
            this.cancel();
            this.clickActivated = false;
        }
    }

    protected searchKommunBeteckning() {
        this.mapFastighetService.enableKarta();
        this.mapFastighetService.searchByKommunBeteckning(this.kommun?.namn, this.fastighetsbeteckning);
    }

    private cancel() {
        this.mapFastighetService.disableKarta();
        this.mapPopupService.close();
    }

    private openPopupOnGeometry(geometry: IMapGeometry): void {
        this.mapFastighetService.openPopupOnGeometry(null, geometry, this.importEnabled);
    }

    private reset() {
        this.panelFormExpanded = false;
        this.panelMapExpanded = false;
        this.clickActivated = false;
        this.mapFastighetService.disableKarta();
        this.kommun = null;
        this.fastighetsbeteckning = "";
        this.mapPopupService.close();
    }

    private selectFastighet(event) {
        if (this.panelFormExpanded !== true && this.panelMapExpanded !== true) {
            return;
        }
        if (isNil(event.geometry)) {
            return;
        }

        this.openPopupOnGeometry(event.geometry);
    }
}
